import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  Handshake,
  Users,
  HeartPulse,
  Repeat,
  ClipboardList,
  ShieldCheck,
  FileCheck,
  MapPin,
  Phone,
  Building2,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/referral-partners';

const ReferralPartners = () => {
  const seo = {
    title: 'Referral Partners | Healthcare & Community NEMT Referrals | MoveraCare',
    description: 'Referral partnership program for healthcare providers, community organizations, and coordinators referring patients for non-emergency medical transportation across Greater Houston.',
    canonical: CANONICAL,
    schemaId: 'partner-referral-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Healthcare Referral Partner Program',
      serviceType: 'Non-Emergency Medical Transportation Referrals',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'BusinessAudience',
        audienceType: 'Healthcare Providers, Community Organizations, Referral Sources',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-referral-partners"
      seo={seo}
      eyebrow="REFERRAL PARTNERS"
      heading="A Trusted NEMT Provider Your Referrals Can Depend On"
      intro="MoveraCare Mobility works with healthcare providers, community organizations, and coordinators who refer patients for non-emergency medical transportation. Refer with confidence — your patients get a reliable wheelchair or ambulatory ride, and you get a provider you can trust."
      audience={{
        title: 'For Providers & Organizations That Refer Patients for NEMT',
        description: 'This page is for anyone whose role includes recommending or referring a non-emergency medical transportation provider to a patient, family, or client.',
        bullets: [
          'Physician offices and outpatient clinics',
          'Senior centers, ALFs, and community-based programs',
          'Home health agencies and hospice teams',
          'Church, faith, and non-profit patient support programs',
          'Adult day services and community rehabilitation programs',
          'Care coordinators, patient navigators, and social workers',
        ],
      }}
      problem={{
        title: 'Refer Once, Trust the Result',
        description: 'When you refer a patient to a transportation provider, that provider becomes an extension of your reputation. MoveraCare is built to protect that trust with reliable, dignified, HIPAA-conscious operations.',
        bullets: [
          'Wheelchair and ambulatory transportation for referred patients',
          'Clear communication with referring organizations when needed',
          'Recurring transportation for referred patients on treatment plans',
          'Dedicated referral pathway for community and provider organizations',
          'Simple referral workflow through the Request Patient Transportation form',
        ],
      }}
      capabilities={{
        title: 'Referral-Ready NEMT Services',
        items: [
          { icon: Handshake, title: 'Referred Patient Intake', desc: 'A simple intake pathway for organizations referring patients for non-emergency transportation.' },
          { icon: Repeat, title: 'Recurring Referral Support', desc: 'Support for patients requiring recurring transportation to treatment or care destinations.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Wheelchair-accessible vans and comfortable ambulatory transport; drivers assist passengers during transport.' },
          { icon: Building2, title: 'Community Program Coordination', desc: 'Flexible coordination for community-based programs and non-profit partners.' },
          { icon: ClipboardList, title: 'Trip Records', desc: 'Trip documentation available to support care plans, funding requirements, or grant reporting.' },
          { icon: Phone, title: 'Direct Provider Relations Contact', desc: 'One provider relations contact for organizational referral questions and escalations.' },
        ],
      }}
      coordination={{
        title: 'How a Referral Becomes a Ride',
        steps: [
          { title: 'Referral Submitted', desc: 'Referral partner submits patient details through the Request Patient Transportation form or contacts dispatch.' },
          { title: 'Intake & Confirmation', desc: 'MoveraCare confirms mobility level, destination, schedule, and any authorization or payer information.' },
          { title: 'Transportation Completed', desc: 'A trained driver completes the trip with wheelchair or ambulatory transportation as appropriate.' },
          { title: 'Ongoing Relationship', desc: 'Referring organizations can send additional referrals through the same workflow or a dedicated contact.' },
        ],
      }}
      why={{
        title: 'Why Referral Partners Trust MoveraCare',
        items: [
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport — dignified support your patients deserve.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory Fleet', desc: 'Wheelchair-accessible vans and ambulatory options for a range of mobility needs.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Operations', desc: 'Professional handling of patient information across dispatch, driver, and destination.' },
          { icon: FileCheck, title: 'Credentialing-Ready Documentation', desc: 'Licensing, insurance, and vendor documentation available for organizational review.' },
          { icon: Phone, title: 'Responsive Communication', desc: 'Clear communication with referring organizations when scheduling adjustments are needed.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Verified service area supporting Fort Bend and surrounding Greater Houston counties.' },
        ],
      }}
      transportRequest={{
        title: 'Submit a Referral',
        description: 'Referral partners can submit patient referrals through the Request Patient Transportation form or by calling dispatch directly. Both one-time and recurring referrals are supported.',
        cta: { label: 'Submit a Referral', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Formalize a Referral Partnership',
        description: 'Organizations, providers, and community programs interested in a formal referral relationship can download MoveraCare’s vendor procurement packet or contact Provider Relations to align on process, communication, and reporting.',
      }}
    />
  );
};

export default ReferralPartners;
